import {
  HttpLink,
  ApolloLink,
  ApolloClient,
  InMemoryCache
} from '@apollo/client';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { ApolloProvider, createApolloProvider } from '@vue/apollo-option';
import { createClient } from 'graphql-ws';
import { createPinia } from 'pinia';
import { createApp } from 'vue';
import { filters } from './filters';
import router from './router';
import { TokenService } from './services/TokenService';
import App from './App.vue';
import { vuei18n } from './plugins/i18n';
import { vuetify } from './plugins/vuetify';

import '@/styles/global.scss';
import '@/styles/vuetify_override.scss';
import { fetchOauth2WellKnownConfiguration } from './services/OidcService';
import AuthApi from './services/auth/AuthApi';

export let apolloProvider: ApolloProvider;
export const createPiniaStore = () => createPinia();

export const createGraphQL = async () => {
  const accessTokenValue = await TokenService.getAccessToken();

  const httpLink = new HttpLink({
    uri: filters.getGraphqlUrlHTTP(),
    headers: {
      authorization: accessTokenValue ? `Bearer ${accessTokenValue}` : ''
    }
  });

  const authMiddleware = new ApolloLink((operation, forward) => {
    operation.setContext({
      authorization: accessTokenValue ? `${accessTokenValue}` : ''
    });
    return forward(operation);
  });

  const authHttpLink = ApolloLink.from([authMiddleware, httpLink]);

  const wsLink = new GraphQLWsLink(
    createClient({
      url: filters.getGraphqlUrlWS(),
      connectionParams: () => ({
        authorization: accessTokenValue ? `${accessTokenValue}` : ''
      })
    })
  );

  // using the ability to split links, you can send data to each link
  // depending on what kind of operation is being sent
  const link = ApolloLink.split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return (
        definition.kind === 'OperationDefinition' &&
        definition.operation === 'subscription'
      );
    },
    wsLink,
    authHttpLink
  );

  // Create the apollo client
  const apolloClient = new ApolloClient({
    link: link, // No need to concat authMiddleware here
    cache: new InMemoryCache()
  });

  return createApolloProvider({
    defaultClient: apolloClient
  });
};

export const refreshGraphQLClient = async () => {
  const newApolloProvider = await createGraphQL();
  apolloProvider.defaultClient = newApolloProvider.defaultClient;
  await apolloProvider.defaultClient.resetStore();
};

export const createVueApp = async () => {
  await fetchOauth2WellKnownConfiguration();
  apolloProvider = await createGraphQL();
  const app = createApp(App)
    .use(vuetify)
    .use(vuei18n)
    .use(router)
    .use(createPiniaStore())
    .use(apolloProvider);

  const applyGlobalsToComponent = (comp: typeof app) => {
    Object.entries(filters).forEach(([name, filter]) => {
      if (!comp.config.globalProperties.$filters) {
        comp.config.globalProperties.$filters = {};
      }
      comp.config.globalProperties.$filters[name] = filter;
    });
  };

  applyGlobalsToComponent(app);
  app.mount('#app');
  await AuthApi.checkIfAuthStateIsValid();

  return app;
};
